import { useCallback, useEffect, useRef, useState } from 'react';

export interface RoutePlayback {
    /** The person being replayed, null when none */
    uid: string | null;
    /** 0–1 of the replay */
    progress: number;
    running: boolean;
    /** Starts `uid` from the beginning, or resumes it when paused mid-way */
    play: (uid: string, durationMs: number) => void;
    pause: () => void;
    stop: () => void;
}

/** One replay at a time, driven by animation frames; the clock moves linearly */
export const useRoutePlayback = (): RoutePlayback => {
    const [uid, setUid] = useState<string | null>(null);
    const [progress, setProgress] = useState(0);
    const [running, setRunning] = useState(false);
    const durationRef = useRef(1);
    const progressRef = useRef(0);
    const uidRef = useRef<string | null>(null);

    useEffect(() => {
        if (!running) return;
        let frameId = 0;
        let last: number | null = null;
        const tick = (now: number) => {
            if (last !== null) {
                progressRef.current = Math.min(1, progressRef.current + (now - last) / durationRef.current);
                setProgress(progressRef.current);
            }
            last = now;
            if (progressRef.current >= 1) setRunning(false);
            else frameId = requestAnimationFrame(tick);
        };
        frameId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frameId);
    }, [running]);

    const play = useCallback((next: string, durationMs: number) => {
        durationRef.current = Math.max(1, durationMs);
        if (uidRef.current !== next || progressRef.current >= 1) {
            progressRef.current = 0;
            setProgress(0);
        }
        uidRef.current = next;
        setUid(next);
        setRunning(true);
    }, []);

    const pause = useCallback(() => setRunning(false), []);

    const stop = useCallback(() => {
        setRunning(false);
        uidRef.current = null;
        setUid(null);
        progressRef.current = 0;
        setProgress(0);
    }, []);

    return { uid, progress, running, play, pause, stop };
};
